import { useCallback, useEffect, useRef } from 'react';
import type { CustomChartTableView } from '@/entities/dashboards';
import { stableStringify } from '@/shared/lib';
import { useChartTableViewStore } from '../ChartTableViewContext';

/**
 * Задержка записи вида колонок: ширина колонки меняется перетаскиванием, а у совместного дашборда
 * владельца запись уходит на сервер.
 */
const VIEW_WRITE_DELAY_MS = 500;

type Params = {
  /** Диаграмма, чьей таблице принадлежит вид колонок */
  chartId: string;
  /** Вид колонок в составе дашборда; нет — все колонки выборки в её порядке */
  dashboardView: CustomChartTableView | undefined;
  /** Выставляет гриду вид колонок, пришедший не от него самого: правка владельца, «Очистить» */
  onApplyView: (view: CustomChartTableView | undefined) => void;
};

/**
 * Вид колонок таблицы диаграммы: какой показать и куда записать смену.
 *
 * Показан свой вид смотрящего, если он его менял, иначе — вид дашборда. Сменой вида владеет сам грид:
 * запись уходит с задержкой, а до неё грид показывает уже выбранное, и записанное, вернувшись, его не
 * переставляет. Вид, сменившийся не от грида, — правка того, кто дашборд правит, дошедшая до
 * смотрящего, — грид получает сразу.
 */
export const useChartTableView = ({ chartId, dashboardView, onApplyView }: Params) => {
  const store = useChartTableViewStore();
  const view = store?.readOwnView(chartId) ?? dashboardView;
  const signature = stableStringify(view ?? null);

  // Вид, который показывает грид: смена, пришедшая от него самого, его же и не переставляет
  const shownRef = useRef(signature);
  const viewRef = useRef(view);
  viewRef.current = view;
  const dashboardViewRef = useRef(dashboardView);
  dashboardViewRef.current = dashboardView;
  const applyRef = useRef(onApplyView);
  applyRef.current = onApplyView;
  const storeRef = useRef(store);
  storeRef.current = store;

  useEffect(() => {
    if (shownRef.current === signature) return;
    shownRef.current = signature;
    applyRef.current(viewRef.current);
  }, [signature]);

  // Отложенная запись: её доводит до места и уход таблицы со страницы раньше срока
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<CustomChartTableView | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    pendingRef.current = null;
  }, []);

  const flushPending = useCallback(() => {
    const pending = pendingRef.current;
    cancelPending();
    if (pending) storeRef.current?.onChange(chartId, pending);
  }, [chartId, cancelPending]);

  useEffect(() => flushPending, [flushPending]);

  /** Пользователь сменил вид колонок в гриде: грид уже показывает его, запись уходит с задержкой. */
  const changeView = useCallback(
    (next: CustomChartTableView) => {
      shownRef.current = stableStringify(next);
      cancelPending();
      pendingRef.current = next;
      timerRef.current = setTimeout(flushPending, VIEW_WRITE_DELAY_MS);
    },
    [cancelPending, flushPending],
  );

  /**
   * «Очистить» в панели колонок: у того, кто вид колонок меняет у себя, таблица возвращается к виду
   * дашборда, у остальных — ко всем колонкам выборки в исходном порядке.
   */
  const clearView = useCallback(() => {
    cancelPending();
    const shown = storeRef.current?.clearsToDashboardView ? dashboardViewRef.current : undefined;
    shownRef.current = stableStringify(shown ?? null);
    applyRef.current(shown);
    storeRef.current?.onClear(chartId);
  }, [chartId, cancelPending]);

  return {
    /** Вид колонок, которым таблица показана сейчас */
    view,
    changeView,
    clearView,
  };
};
